/**
 * Draw one paper's figures with the playground knobs.
 * The model call is injected so a test does not reach the dev server.
 *
 * @format
 */

import {
	buildWordingPrompt,
	expandFigureWithWording,
	wordingCandidates,
	wordingIndexes,
	type ClusteredFigure,
	type FigureBox,
	type FigureTextRun,
} from "@question-bank/shared";
import { wordingTuneOf, type AssetPlaygroundModel, type AssetPlaygroundSettings } from "./assetPlaygroundSettings";

export interface PlaygroundPageText {
	pageNumber: number;
	items: FigureTextRun[];
}

export interface PlaygroundFigureCard {
	page: number;
	/** Crop before any wording is drawn. */
	beforeSvg: string;
	/** Crop with every offered candidate drawn (the model-free result). */
	svg: string;
	/** Crop with only the model's chosen subset drawn. Set when the model is asked. */
	svgWithModel?: string;
	/** Raster trimmed to the figure and the included word boxes. */
	rasterTrimIncludedPng?: string;
	/** Boxes of the included runs, for trimming a raster crop. */
	includedBoxes: FigureBox[];
	prompt: string;
	candidates: string[];
	/** Words drawn without the model (every offered candidate). */
	included: string[];
	/** Words the model chose. Set when the model is asked. */
	includedWithModel?: string[];
	askedModel: boolean;
	modelError?: string;
}

const POOL_LIMIT = 3;

const mapPool = async <T, R>(items: readonly T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> => {
	const results = new Array<R>(items.length);
	let next = 0;
	const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (next < items.length) {
			const index = next;
			next += 1;
			results[index] = await task(items[index]);
		}
	});
	await Promise.all(workers);
	return results;
};

/** Ask the local dev server to run one wording prompt. The server holds the platform secret. */
export const askPlaygroundModel = async (prompt: string, model: AssetPlaygroundModel): Promise<string> => {
	const response = await fetch("/api/asset-playground/wording", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ prompt, model }),
	});
	const body = (await response.json()) as { response?: unknown; error?: unknown };
	if (!response.ok || typeof body.response !== "string") {
		throw new Error(typeof body.error === "string" ? body.error : "The model call failed.");
	}
	return body.response;
};

export const drawPlaygroundFigures = async (
	settings: AssetPlaygroundSettings,
	pages: readonly PlaygroundPageText[],
	figures: readonly ClusteredFigure[],
	askModel: (prompt: string, model: AssetPlaygroundModel) => Promise<string>,
): Promise<PlaygroundFigureCard[]> => {
	const tune = wordingTuneOf(settings);
	return mapPool(figures, settings.shouldAskModel ? POOL_LIMIT : figures.length, async (figure) => {
		const items = pages.find((page) => page.pageNumber === figure.page)?.items ?? [];
		const candidates = wordingCandidates(figure, items, tune, figure.page);
		const prompt = buildWordingPrompt({
			language: settings.language,
			figure,
			candidates,
			instructions: settings.prompt,
			lineFormat: settings.lineFormat,
		});
		const toBox = (run: FigureTextRun): FigureBox => ({
			x: run.x,
			y: run.y,
			width: run.width,
			height: run.height,
		});

		// The model-free result draws every offered candidate. This is the path
		// the deterministic filter produces and the one we aim to keep.
		const noModelRuns = candidates;
		const noModelDrawn = expandFigureWithWording(figure, noModelRuns, tune);

		// When asked, also compute the model's chosen subset, so the two sit side
		// by side for comparison. One model call per figure, as before.
		let modelRuns: FigureTextRun[] | undefined;
		let modelError: string | undefined;
		if (settings.shouldAskModel && candidates.length > 0) {
			try {
				const raw = await askModel(prompt, settings.model);
				const indexes = wordingIndexes(raw, candidates.length);
				modelRuns = indexes.flatMap((index) => {
					const run = candidates[index];
					return run ? [run] : [];
				});
			} catch (error) {
				modelError = error instanceof Error ? error.message : "The model call failed.";
				modelRuns = [];
			}
		}
		const modelDrawn = modelRuns !== undefined ? expandFigureWithWording(figure, modelRuns, tune) : undefined;

		return {
			page: figure.page,
			beforeSvg: figure.svg,
			svg: noModelDrawn.svg,
			...(modelDrawn ? { svgWithModel: modelDrawn.svg } : {}),
			// The raster trim follows the model-free result, the recommended path.
			includedBoxes: noModelRuns.map(toBox),
			prompt,
			candidates: candidates.map((run) => run.text),
			included: noModelRuns.map((run) => run.text),
			...(modelRuns !== undefined ? { includedWithModel: modelRuns.map((run) => run.text) } : {}),
			askedModel: settings.shouldAskModel,
			...(modelError ? { modelError } : {}),
		};
	});
};

/** A stable key for a figure's page region, matching the raster region key. */
export const figureKey = (figure: { page: number; x: number; y: number }): string =>
	`${figure.page}:${Math.round(figure.x)}:${Math.round(figure.y)}`;
